/**
 * Canonical message formatting — WhatsApp-like markers, XSS-safe render model.
 * Markers: *bold* _italic_ ~strike~ `mono`
 */

export type FormatSpan =
  | { kind: "text"; text: string }
  | { kind: "bold" | "italic" | "strike" | "mono"; text: string };

const RULES: { kind: FormatSpan["kind"]; re: RegExp }[] = [
  { kind: "mono", re: /`([^`]+)`/ },
  { kind: "bold", re: /\*([^*]+)\*/ },
  { kind: "italic", re: /_([^_]+)_/ },
  { kind: "strike", re: /~([^~]+)~/ },
];

/** Parse plaintext with markers into a safe span tree (no HTML). */
export function parseFormatted(input: string): FormatSpan[] {
  if (!input) return [];
  const out: FormatSpan[] = [];
  let rest = input;
  while (rest.length) {
    let best: { index: number; length: number; span: FormatSpan } | null = null;
    for (const rule of RULES) {
      if (rule.kind === "text") continue;
      const m = rule.re.exec(rest);
      if (!m || m.index === undefined) continue;
      const candidate = {
        index: m.index,
        length: m[0].length,
        span: { kind: rule.kind as Exclude<FormatSpan["kind"], "text">, text: m[1]! },
      };
      if (!best || candidate.index < best.index) best = candidate;
    }
    if (!best) {
      out.push({ kind: "text", text: rest });
      break;
    }
    if (best.index > 0) out.push({ kind: "text", text: rest.slice(0, best.index) });
    out.push(best.span);
    rest = rest.slice(best.index + best.length);
  }
  return out;
}

/** Strip markers for search / previews. */
export function plainPreview(input: string): string {
  return parseFormatted(input)
    .map((s) => s.text)
    .join("");
}

/** Single-line conversation-list preview (markers stripped, truncated). */
export function listPreview(input: string, maxLen = 96): string {
  const plain = plainPreview(input).replace(/\s+/g, " ").trim();
  if (!plain) return "";
  return plain.length <= maxLen ? plain : `${plain.slice(0, maxLen - 1)}…`;
}

/**
 * Render spans to DOM-safe React children via callbacks — never concatenates HTML.
 * Callers must use text nodes / React elements only.
 */
export function mapFormatted<T>(
  input: string,
  render: (span: FormatSpan, index: number) => T,
): T[] {
  return parseFormatted(input).map(render);
}

/** Reject common XSS payloads if someone mistakenly string-builds HTML. */
export function containsDangerousHtml(input: string): boolean {
  return /<\s*script|javascript:|onerror\s*=|onload\s*=|<\s*iframe/i.test(input);
}
